import React from 'react';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import { createBrowserHistory } from 'history';
import { loadTranslations, setLocale, syncTranslationWithStore } from 'react-redux-i18n';
import moment from 'moment';
import axios from 'axios';

import JssProvider from 'react-jss/lib/JssProvider';
import { create } from 'jss';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';

import AppContainer from 'containers/AppContainer';

// Store.
import configureStore from 'store/configureStore';

// Utils.
import { getCookie, setCookie, getLanguage } from 'utils';
import { getAccessTokenFromBrowserCookie } from 'utils/auth';
import {
  hydrateJwtSessionOnBoot,
  setupAxiosAuthInterceptors,
} from 'utils/axiosAuth';

// Constants.
import * as AuthConstants from 'constants/AuthConstants';

// Languages.
import locales from 'locales';

// Current browser language.
const language = getLanguage();

// Config.
import { apiURL, sentryURL } from './config';

moment.updateLocale(language, {
  week: {
    dow: language === 'ru' ? 1 : 0,
  }
});

axios.defaults.baseURL = apiURL;
axios.defaults.withCredentials = true;

setCookie('lang', language);

const csrftoken = getCookie('csrftoken');
if (csrftoken) {
  axios.defaults.headers.post['X-CSRFToken'] = csrftoken;
  axios.defaults.headers.put['X-CSRFToken'] = csrftoken;
  axios.defaults.headers.patch['X-CSRFToken'] = csrftoken;
  axios.defaults.headers.delete['X-CSRFToken'] = csrftoken;
}
axios.defaults.headers.common['Accept-Language'] = language;

setupAxiosAuthInterceptors();

if (sentryURL && window.Raven !== undefined) {
  window.Raven.config(sentryURL).install();
}

const initialState = window.__initialState__ || undefined;

const history = createBrowserHistory();

const store = configureStore(initialState, history);

// const history = syncHistoryWithStore(browserHistory, store);

syncTranslationWithStore(store, history);

store.dispatch(loadTranslations(locales));
store.dispatch(setLocale(language));

const generateClassName = createGenerateClassName();
const jss = create(jssPreset());
// We define a custom insertion point that JSS will look for injecting the styles in the DOM.
// jss.options.insertionPoint = 'custom-insertion-point';
jss.setup({insertionPoint: 'custom-insertion-point'})

function renderApp() {
  render(
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <JssProvider jss={jss} generateClassName={generateClassName}>
          <AppContainer />
        </JssProvider>
      </ConnectedRouter>
    </Provider>,
    document.getElementById('root')
  );
}

// Restore access_token from httpOnly refresh before AuthRequireContainer
// may set anonymous `Authorization: Token` and skip the JWT pipeline.
// Auth reducer reads cookies at import time — sync Redux after hydrate.
function syncAuthStateAfterHydrate() {
  const hasJwt = Boolean(getAccessTokenFromBrowserCookie());
  const hasSession = Boolean(
    getCookie('csrftoken') || getCookie('welltorysessionid')
  );
  if ((hasJwt || hasSession) && !store.getState().auth.isAuthenticated) {
    store.dispatch({
      type: AuthConstants.LOGIN_SUCCESS,
      userType: 'full',
    });
  }
}

hydrateJwtSessionOnBoot()
  .then(() => {
    syncAuthStateAfterHydrate();
    renderApp();
  })
  .catch(() => {
    syncAuthStateAfterHydrate();
    renderApp();
  });
